import React, { useEffect, useMemo, useState } from 'react'
import { IoMdCloseCircleOutline } from 'react-icons/io'

interface NumberInputWithNullProps {
  disabled?: boolean
  value: number | null
  className?: string
  placeholder?: string
  min?: number
  max?: number
  step?: number
  precision?: number // decimals to render (default 0 when asPercent, else undefined)
  asPercent?: boolean // if true, renders a % suffix (value is still 0–100)
  onChange: (val: number | null) => void
}

const clamp = (n: number, min?: number, max?: number) => {
  if (min != null && n < min) {
    return min
  }
  if (max != null && n > max) {
    return max
  }

  return n
}

const formatNumber = (n: number, precision?: number) => {
  if (precision == null) {
    return String(n)
  }

  return n.toFixed(precision)
}

const NumberInputWithNull: React.FC<NumberInputWithNullProps> = ({
  disabled = false,
  className,
  placeholder,
  min,
  max,
  step,
  value,
  precision,
  asPercent,
  onChange,
}) => {
  const [inputValue, setInputValue] = useState(value?.toString() || '')

  const effPrecision = useMemo(() => {
    if (precision != null) {
      return precision
    }

    return asPercent ? 0 : undefined
  }, [precision, asPercent])

  useEffect(() => {
    setInputValue(value?.toString() || '')
  }, [value])

  const parseToNumber = (raw: string): number | null => {
    if (raw.trim() === '') {
      return null
    }
    const cleaned = raw.replace(/[%\s,]/g, '')
    const num = Number.parseFloat(cleaned)

    return Number.isFinite(num) ? num : null
  }

  const emitClamped = (maybeNum: number | null) => {
    if (maybeNum == null) {
      onChange(null)

      return
    }
    const c = clamp(maybeNum, min, max)
    onChange(c)
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    setInputValue(newValue)

    const parsed = parseToNumber(newValue)
    if (parsed === null) {
      onChange(null)

      return
    }
    emitClamped(parsed)
  }

  const handleInputBlur = () => {
    const parsed = parseToNumber(inputValue)
    if (parsed == null) {
      setInputValue('')
      onChange(null)

      return
    }
    const clamped = clamp(parsed, min, max)
    setInputValue(formatNumber(clamped, effPrecision))
    onChange(clamped)
  }

  const handleClearClick = () => {
    setInputValue('')
    onChange(null)
  }

  const handleSetValue = () => {
    const next = clamp(0, min, max)
    setInputValue(formatNumber(next, effPrecision))
    onChange(next)
  }

  return (
    <div className={`relative flex items-center gap-2 ${className}`}>
      {value === null ? (
        <div
          className={`flex-1 flex items-center justify-between px-3 py-2 bg-background rounded-md border border-solid border-gray-500 font-semibold ${
            disabled ? 'cursor-not-allowed opacity-60' : ''
          }`}
        >
          <span>None</span>
          <button
            type="button"
            onClick={handleSetValue}
            disabled={disabled}
            className={`h-auto p-1 text-xs rounded-md outline-none ${
              disabled ? 'cursor-not-allowed opacity-60' : 'hover:bg-gray-300'
            }`}
            onMouseDown={(e) => e.preventDefault()}
          >
            Set Value
          </button>
        </div>
      ) : (
        <div className="h-full flex items-center justify-between w-full">
          <div
            style={{ width: '85%' }}
            className="relative flex items-center flex-row"
          >
            <input
              type="number"
              value={inputValue}
              onChange={handleInputChange}
              onBlur={handleInputBlur}
              placeholder={placeholder}
              min={min}
              max={max}
              step={step}
              disabled={disabled}
              className={`h-full py-3 font-semibold border-solid border border-gray-500 rounded-md pl-3 w-full ${
                asPercent ? 'pr-10' : ''
              } ${disabled ? 'cursor-not-allowed opacity-60' : ''}`}
              onWheel={(e) => e.preventDefault()}
            />
            {asPercent && (
              <span style={{ right: '20px', position: 'relative' }}>%</span>
            )}
          </div>
          <button
            type="button"
            onClick={handleClearClick}
            disabled={disabled}
            className={`h-full p-3 bg-transparent border border-solid border-gray-500 rounded-md w-10p flex flex-col items-center outline-none ${
              disabled ? 'cursor-not-allowed opacity-60' : 'hover:bg-gray-300'
            }`}
            title="Clear value"
            onMouseDown={(e) => e.preventDefault()}
          >
            <IoMdCloseCircleOutline size={22} />
          </button>
        </div>
      )}
    </div>
  )
}

export default NumberInputWithNull
